import React from 'react';
import { Link } from 'react-router-dom';
import BackLink from './BackLink';
import Footer from './Footer';
import './Changelog.css';

// Newest first. Add a week at the top when a release goes out.
// kind: new | improved | fixed. `link` points to a page that explains the feature.
export const CHANGELOG = [
  {
    week: 'Week of 5 October 2026',
    version: '1.2',
    title: 'A friendlier first run',
    items: [
      { kind: 'new', text: 'New setup: pick your language and voice, practise the wake word, and try a first task, all in a few steps.' },
      { kind: 'new', text: 'Four new wake words to choose from, each with its own look.' },
      { kind: 'new', text: 'YouTube Skip Helper taps YouTube’s own Skip ad button for you when it appears.', link: '/why-yt-a11y-skip-helper' },
      { kind: 'new', text: 'Maps Ride Helper opens Uber or Rapido from Google Maps with your destination filled in.', link: '/why-maps-ride-helper' },
      { kind: 'improved', text: 'Chat Reply Helper drafts replies faster, and your first 100 drafts are free.', link: '/why-chat-reply-helper' },
      { kind: 'improved', text: 'A more natural default voice for English and other non-Hindi languages. You can still pick any voice.' },
      { kind: 'improved', text: 'The assistant overlay has a cleaner layout.' },
      { kind: 'improved', text: 'Pro turns on right after you buy, including payments that take a while to clear.' },
      { kind: 'improved', text: 'Panda tells you when your app version is too old and how to update.' },
    ],
  },
  {
    week: 'Week of 28 September 2026',
    title: 'Panda gets reflexes',
    items: [
      {
        kind: 'new',
        text: 'A fast decision layer in front of the AI model. Panda picks most taps and app opens in about 0.4 seconds, and simple requests like “what’s my battery” answer in under half a second.',
        link: '/blog/panda-gets-reflexes',
        linkLabel: 'Read the story',
      },
      { kind: 'new', text: 'New wake word: “Hey Jarvis”.' },
      { kind: 'new', text: 'Chat Reply Helper: tap the button above your keyboard and Panda drafts a reply. You always send it yourself.', link: '/why-chat-reply-helper' },
      { kind: 'new', text: 'A morning weather notification, if you turn it on in Settings.' },
      { kind: 'new', text: 'A live card shows what Panda is doing during a task in plain words, then a summary you can rate.' },
      { kind: 'improved', text: 'Panda asks you to confirm before it sends a message for you.' },
      { kind: 'improved', text: 'Panda remembers the language you picked.' },
      { kind: 'fixed', text: 'Static or radio noise in spoken replies.' },
      { kind: 'fixed', text: 'Teach Panda failing to load some saved memories.' },
    ],
  },
  {
    week: 'Week of 21 September 2026',
    title: 'Faster listening',
    items: [
      { kind: 'improved', text: 'Speech recognition now streams while you talk, so Panda starts answering sooner.' },
      { kind: 'improved', text: 'If one speech service is down, Panda switches to another on its own.' },
      { kind: 'new', text: 'A short how-to video plays while you turn on Panda’s accessibility service.' },
    ],
  },
  {
    week: 'Week of 14 September 2026',
    title: 'Quicker replies',
    items: [
      { kind: 'improved', text: 'Conversational replies arrive faster, and Panda starts speaking before the whole answer is ready.' },
      { kind: 'new', text: 'A new natural English voice.' },
      { kind: 'new', text: 'Report a problem from Settings or after a task. An assistant helps right away and passes it to our team.' },
    ],
  },
];

const KIND_LABEL = {
  new: 'Added',
  improved: 'Improved',
  fixed: 'Fixed',
};

export default function Changelog() {
  return (
    <div className="notes-page">
      <div className="notes-inner">
        <BackLink to="/blog" className="notes-back">Blog</BackLink>
        <h1>Changelog</h1>
        <p className="notes-lede">
          Release notes for Panda, including new features, improvements, and fixes. Newest first.
          Updates reach phones through Google Play over a few days.
        </p>

        <ol className="notes-list">
          {CHANGELOG.map((week) => (
            <li key={week.week} className="notes-release">
              <h2>{week.version ? week.version : week.title}</h2>
              <time className="notes-date">{week.week}</time>
              {week.version && <p className="notes-summary">{week.title}</p>}
              <ul>
                {week.items.map((item) => (
                  <li key={item.text}>
                    {KIND_LABEL[item.kind]}: {item.text}
                    {item.link && (
                      <>
                        {' '}
                        <Link to={item.link}>{item.linkLabel || 'Learn more'}</Link>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>

        <p className="notes-foot">
          Want the story behind a feature? Read the <Link to="/blog">blog</Link>.
        </p>
      </div>
      <Footer />
    </div>
  );
}
